import { Button } from "@/components/ui/button";
import { HERO_IMAGES } from "@/mock";
import { ArrowRight, MessageCircle, Dot } from "lucide-react";
import { BRAND } from "@/mock";

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const Hero = () => {
  return (
    <section className="relative overflow-hidden bg-[#F9F7F3]" data-testid="hero-section">
      {/* decorative background */}
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(#1A1A1A 1px, transparent 1px), linear-gradient(90deg, #1A1A1A 1px, transparent 1px)",
          backgroundSize: "64px 64px",
        }}
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 80% 20%, rgba(200,169,126,0.18) 0%, transparent 50%), radial-gradient(ellipse at 10% 90%, rgba(200,169,126,0.08) 0%, transparent 50%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto grid lg:grid-cols-2 gap-10 lg:gap-16 px-5 md:px-10 py-14 lg:py-24 items-center">
        {/* Left */}
        <div className="relative z-10">
          <div
            className="inline-flex items-center gap-2 bg-white/70 backdrop-blur border border-[#E5E0D8] rounded-full px-4 py-1.5 mb-7"
            data-testid="hero-tag"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#1a7a42] blink" />
            <span className="text-[10px] tracking-[0.22em] uppercase text-[#1A1A1A] font-medium">
              Now Open · Bangalore
            </span>
          </div>

          <h1 className="font-serif text-[52px] sm:text-[64px] lg:text-[76px] leading-[0.98] text-[#1A1A1A] mb-2 font-semibold">
            Hitha<br />
            <span className="italic text-[#C8A97E] font-normal">Residency</span>
          </h1>
          <div className="font-serif text-[16px] sm:text-[18px] tracking-[0.32em] uppercase text-[#9A9A9A] mb-6">
            Service Apartment
          </div>
          <div className="w-12 h-[2px] bg-gradient-to-r from-[#C8A97E] to-transparent mb-6" />

          <p className="text-[15px] sm:text-[16px] text-[#555] leading-[1.8] mb-8 max-w-[460px]">
            <span className="text-[#1A1A1A] font-medium">Comfort · Convenience · Luxury</span>
            <br />
            Fully furnished service apartments at Chikkbidrekallu, Tumkur Road.
            <br />
            Daily &amp; Monthly rental basis.
          </p>

          <div className="flex flex-wrap gap-3 mb-12">
            <Button
              onClick={() => scrollToId("rooms")}
              className="bg-[#1A1A1A] hover:bg-[#2A2A2A] text-white rounded-full px-7 h-12 text-[13px] font-semibold tracking-wide shadow-[0_10px_28px_rgba(26,26,26,0.2)]"
              data-testid="hero-view-rooms-btn"
            >
              View Rooms <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
            <a
              href={`https://wa.me/${BRAND.whatsapp}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-[#C8A97E] text-[#C8A97E] hover:bg-[#C8A97E] hover:text-white transition-all px-7 h-12 text-[13px] font-medium"
              data-testid="hero-whatsapp-btn"
            >
              <MessageCircle className="w-4 h-4" />
              WhatsApp Us
            </a>
          </div>

          <div className="flex gap-8 pt-7 border-t border-[#E5E0D8]">
            {[
              { num: "5", label: "Room Types" },
              { num: "10+", label: "Facilities" },
              { num: "24/7", label: "Security" },
            ].map((s) => (
              <div key={s.label} data-testid={`hero-stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}>
                <div className="font-serif text-[34px] font-semibold text-[#1A1A1A] leading-none">
                  {s.num}
                </div>
                <div className="text-[10px] tracking-[0.18em] uppercase text-[#9A9A9A] mt-1.5">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right - image mosaic */}
        <div className="relative z-10 grid grid-cols-2 grid-rows-[1.2fr_1fr] gap-3 h-[440px] sm:h-[520px] hidden md:grid">
          <div className="col-span-2 relative overflow-hidden rounded-[18px] rounded-b-none group">
            <img
              src={HERO_IMAGES[0].url}
              alt={HERO_IMAGES[0].label}
              className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
              data-testid="hero-image-0"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/40" />
            <div className="absolute bottom-3 left-4 text-[10px] tracking-[0.22em] uppercase text-white/90 font-medium">
              {HERO_IMAGES[0].label}
            </div>
          </div>
          <div className="relative overflow-hidden rounded-bl-[18px] group">
            <img
              src={HERO_IMAGES[1].url}
              alt={HERO_IMAGES[1].label}
              className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
              data-testid="hero-image-1"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/40" />
            <div className="absolute bottom-3 left-4 text-[10px] tracking-[0.22em] uppercase text-white/90 font-medium">
              {HERO_IMAGES[1].label}
            </div>
          </div>
          <div className="relative overflow-hidden rounded-br-[18px] group">
            <img
              src={HERO_IMAGES[2].url}
              alt={HERO_IMAGES[2].label}
              className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
              data-testid="hero-image-2"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/40" />
            <div className="absolute bottom-3 left-4 text-[10px] tracking-[0.22em] uppercase text-white/90 font-medium">
              {HERO_IMAGES[2].label}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
